// @ts-strict-ignore
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { RRule } from 'rrule';
import { useImmediateLayoutAnimation } from 'use-layout-animation';
import { View } from '@shared/components/View';
import { MBCalendar } from '@shared/components/dateAndTime/calendar';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody1, MBTextBody2Bold } from '@shared/components/typography';
import { ISOString } from '@shared/scenes/cards/agenda/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { NavStack } from '@shared/util/navigation';
import * as RecurrencePresets from '../../presets';
import { RecurrencePickerRouteNames } from '../RecurrencePickerRoutes';
import { DescriptiveRecurrenceHeader } from './DescriptiveRecurrenceHeader';
import { RepeatEveryLabel } from './RepeatEveryLabel';

interface Props {
  rruleString: string | undefined;
  onChange(rule: RRule | undefined): void;
  nav: NavStack<RecurrencePickerRouteNames>;
}

export function YearlyRecurrencePicker({ rruleString, onChange, nav }: Props) {
  const colors = useContext(ColorThemeContext);
  const rule = useMemo(
    () => (!isEmpty(rruleString) ? RRule.fromString(rruleString) : undefined),
    [rruleString],
  );

  const today = new Date(Date.now());
  const minDate = today.getFullYear() + '-01-01';
  const selectedDate = useMemo(() => {
    if (!isEmpty(rule?.options.bymonth) && !isEmpty(rule?.options.bymonthday)) {
      const date = new Date();
      date.setFullYear(
        today.getFullYear(),
        rule.options.bymonth[0] - 1,
        rule.options.bymonthday[0],
      );
      const offset = date.getTimezoneOffset();
      const offsetDate = new Date(date.getTime() - offset * 60 * 1000);
      return offsetDate.toISOString().split('T')[0];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule]);

  const dateDescription = useMemo(() => {
    const date = new Date();
    date.setFullYear(
      today.getFullYear(),
      rule?.options.bymonth[0] - 1,
      rule?.options.bymonthday[0],
    );
    return date.toLocaleString('default', { month: 'long', day: 'numeric' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const currentDayIndices = rule?.options.bymonthday;
  const handleChange = useCallback(
    (dateString: ISOString) => {
      const date = new Date(dateString);
      onChange(
        RecurrencePresets.yearly(date.getDate(), date.getMonth() + 1, rule?.options.interval),
      );
    },
    [rule, onChange],
  );

  const openIntervalPicker = useCallback(() => {
    nav.push({ name: 'selectYearlyInterval' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImmediateLayoutAnimation([currentDayIndices]);

  return (
    <>
      {!isNil(rule) && <DescriptiveRecurrenceHeader rule={rule} />}
      <RepeatEveryLabel
        intervalValue={rule?.options.interval ?? 1}
        intervalPluralStringKey={'card.blocks.recurrenceInput.yearly.xYears'}
        isPressable={!rule}
        onPress={openIntervalPicker}
      />
      <View paddingHorizontal={3}>
        <MBTextBody2Bold color={colors.foregroundActive}>
          {i18n('card.blocks.recurrenceInput.yearly.recurrenceTitle')}
        </MBTextBody2Bold>
      </View>
      <View paddingHorizontal={3} paddingTop={2}>
        {!isEmpty(rule?.options.bymonthday) && (
          <View row centerVertically paddingBottom={2}>
            <MBIconV2 name="calendar" size={grid(4)} color={colors.accent} />
            <View paddingLeft={2} />
            <MBTextBody1 color={colors.foregroundActive}>{dateDescription}</MBTextBody1>
          </View>
        )}
      </View>
      <View paddingHorizontal={2}>
        <MBCalendar
          minDate={minDate}
          onSelectDate={handleChange}
          initialDate={selectedDate}
          selectedDates={[selectedDate]}
        />
      </View>
    </>
  );
}
